/**
 * Extracts the first frame of a video file as a Blob (JPEG).
 */
export const extractThumbnailFromVideo = (file: File): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    video.src = URL.createObjectURL(file);

    video.onloadedmetadata = () => {
      video.currentTime = 0; // Seek to first frame
    };

    video.onseeked = () => {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      canvas.toBlob((blob) => {
        URL.revokeObjectURL(video.src);
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to capture thumbnail'));
        }
      }, 'image/jpeg', 0.85);
    };

    video.onerror = () => {
      URL.revokeObjectURL(video.src);
      reject(new Error('Failed to load video for thumbnail extraction'));
    };
  });
};

/**
 * Extracts a motion thumbnail (silent video) from the first few seconds of a video file.
 */
export const extractMotionThumbnailFromVideo = (file: File, maxDuration: number = 10): Promise<Blob | File> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'auto';
    video.muted = true;
    video.playsInline = true;
    video.src = URL.createObjectURL(file);

    video.onloadedmetadata = () => {
      const duration = Math.min(video.duration, maxDuration);
      
      // Use captureStream if available (standard in modern browsers)
      const stream = (video as HTMLVideoElement & { captureStream?: () => MediaStream; mozCaptureStream?: () => MediaStream }).captureStream?.() || 
                     (video as HTMLVideoElement & { captureStream?: () => MediaStream; mozCaptureStream?: () => MediaStream }).mozCaptureStream?.();
      
      if (!stream) {
        URL.revokeObjectURL(video.src);
        reject(new Error('Browser does not support video stream capture'));
        return;
      }

      // Remove audio tracks to ensure "no audio"
      const videoOnlyStream = new MediaStream(stream.getVideoTracks());
      
      const mediaRecorder = new MediaRecorder(videoOnlyStream, { 
        mimeType: 'video/webm;codecs=vp9' 
      });

      const chunks: Blob[] = [];
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };

      mediaRecorder.onstop = () => {
        const motionBlob = new Blob(chunks, { type: 'video/webm' });
        URL.revokeObjectURL(video.src);
        resolve(motionBlob);
      };

      // Start recording
      mediaRecorder.start();
      video.play();

      // Stop after duration or if video ends
      const stopRecording = () => {
        if (mediaRecorder.state !== 'inactive') {
          video.pause();
          mediaRecorder.stop();
        }
      };

      video.onended = stopRecording;
      setTimeout(stopRecording, duration * 1000 + 100); // Buffer slightly for timing drift
    };

    video.onerror = () => {
      URL.revokeObjectURL(video.src);
      reject(new Error('Failed to load video for motion thumbnail extraction'));
    };
  });
};
